import type { MultiPaneItem } from '../../multiPane/multiPaneLayout.interface'
import { PredefinedDashboard } from '../../widgetDashboard/widgetDashboard.enums'
import DashboardWrapper from '../../widgetDashboard/predefined/DashboardWrapper'
import { Layout_1 } from '../../widgetDashboard/predefined/dl_1'
import { Layout_h2 } from '../../widgetDashboard/predefined/dl_h2'
import { Layout_h2_1h2_2v2 } from '../../widgetDashboard/predefined/dl_h2_1h2_2v2'
import { Layout_h2_1v2 } from '../../widgetDashboard/predefined/dl_h2_1v2'
import { Layout_h2_1v2_2v2 } from '../../widgetDashboard/predefined/dl_h2_1v2_2v2'
import { Layout_h2_1v3 } from '../../widgetDashboard/predefined/dl_h2_1v3'
import { Layout_h2_2v2 } from '../../widgetDashboard/predefined/dl_h2_2v2'
import { Layout_h2_2v3 } from '../../widgetDashboard/predefined/dl_h2_2v3'
import { Layout_h3 } from '../../widgetDashboard/predefined/dl_h3'
import { Layout_h4 } from '../../widgetDashboard/predefined/dl_h4'
import { Layout_v2 } from '../../widgetDashboard/predefined/dl_v2'
import { Layout_v2_1h2 } from '../../widgetDashboard/predefined/dl_v2_1h2'
import { Layout_v2_1h2_2v2 } from '../../widgetDashboard/predefined/dl_v2_1h2_2v2'
import { Layout_v2_2h2 } from '../../widgetDashboard/predefined/dl_v2_2h2'
import { Layout_v2_2h3 } from '../../widgetDashboard/predefined/dl_v2_2h3'
import { Layout_v3 } from '../../widgetDashboard/predefined/dl_v3'
import { Layout_v4 } from '../../widgetDashboard/predefined/dl_v4'

/**
 * `horizontal: true` on a pane means its children are laid out along the width
 * (side-by-side columns); `false` lays them along the height (stacked rows).
 * See `getPaneStyle` in multipPaneLayout.helper.
 */
export type LayoutOrientation = 'columns' | 'rows'

export type LayoutPreset = {
    id: PredefinedDashboard
    /** Stable identifier the agent uses, e.g. `LAYOUT_H2_2V2`. */
    code: string
    name: string
    /** Derived from the pane tree, so it can never drift from the real config. */
    paneCount: number
    orientation: LayoutOrientation | null
    structure: string
    aliases: string[]
    wrapper: DashboardWrapper
}

type PresetSeed = {
    id: PredefinedDashboard
    code: string
    name: string
    aliases: string[]
    wrapper: DashboardWrapper
}

function leafCount(node: MultiPaneItem): number {
    if (!node.panes.length) return 1
    return node.panes.reduce((total, child) => total + leafCount(child), 0)
}

function unitFor(node: MultiPaneItem): LayoutOrientation {
    return node.horizontal ? 'columns' : 'rows'
}

function describeNode(node: MultiPaneItem): string {
    if (!node.panes.length) return ''
    const orientation = unitFor(node)
    const singular = orientation === 'columns' ? 'column' : 'row'
    const count = node.panes.length
    const head = `${count} ${count === 1 ? singular : orientation}`
    const nested: string[] = []
    node.panes.forEach((child, index) => {
        const childDescription = describeNode(child)
        if (childDescription) {
            nested.push(`${singular} ${index + 1} split into ${childDescription}`)
        }
    })
    return nested.length ? `${head} (${nested.join('; ')})` : head
}

/**
 * Enumerates leaf panes in visual reading order — left-to-right within a column
 * split, top-to-bottom within a row split. This is the order the agent's 1-based
 * `pane` index refers to.
 */
export function leafPanesInVisualOrder(root: MultiPaneItem | undefined): MultiPaneItem[] {
    if (!root) return []
    if (!root.panes.length) return [root]
    return root.panes.flatMap((child) => leafPanesInVisualOrder(child))
}

const PRESET_SEEDS: PresetSeed[] = [
    {
        id: PredefinedDashboard.LAYOUT_1,
        code: 'LAYOUT_1',
        name: 'Single pane',
        aliases: ['single', 'single pane', 'one pane', 'one chart', 'full screen', 'unsplit', 'default'],
        wrapper: new Layout_1(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2,
        code: 'LAYOUT_H2',
        name: '2 columns',
        aliases: ['2 columns', 'two columns', 'side by side', 'left and right', '2 panes side by side'],
        wrapper: new Layout_h2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V2,
        code: 'LAYOUT_V2',
        name: '2 rows',
        aliases: ['2 rows', 'two rows', 'stacked', 'top and bottom', '2 panes stacked'],
        wrapper: new Layout_v2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H3,
        code: 'LAYOUT_H3',
        name: '3 columns',
        aliases: ['3 columns', 'three columns', 'three side by side'],
        wrapper: new Layout_h3(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V3,
        code: 'LAYOUT_V3',
        name: '3 rows',
        aliases: ['3 rows', 'three rows', 'three stacked'],
        wrapper: new Layout_v3(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2_1V2,
        code: 'LAYOUT_H2_1V2',
        name: '2 columns, left split into 2 rows',
        aliases: ['left stacked', 'left column split'],
        wrapper: new Layout_h2_1v2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2_2V2,
        code: 'LAYOUT_H2_2V2',
        name: '2 columns, right split into 2 rows',
        aliases: ['right stacked', 'right column split', 'one big left two right'],
        wrapper: new Layout_h2_2v2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V2_1H2,
        code: 'LAYOUT_V2_1H2',
        name: '2 rows, top split into 2 columns',
        aliases: ['top split', 'two on top one below'],
        wrapper: new Layout_v2_1h2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V2_2H2,
        code: 'LAYOUT_V2_2H2',
        name: '2 rows, bottom split into 2 columns',
        aliases: ['bottom split', 'one on top two below'],
        wrapper: new Layout_v2_2h2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2_1V2_2V2,
        code: 'LAYOUT_H2_1V2_2V2',
        name: '2x2 grid',
        aliases: ['2x2', '2 by 2', 'grid', 'quad', 'four grid', 'square grid'],
        wrapper: new Layout_h2_1v2_2v2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H4,
        code: 'LAYOUT_H4',
        name: '4 columns',
        aliases: ['4 columns', 'four columns', 'four side by side'],
        wrapper: new Layout_h4(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V4,
        code: 'LAYOUT_V4',
        name: '4 rows',
        aliases: ['4 rows', 'four rows', 'four stacked'],
        wrapper: new Layout_v4(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2_1V3,
        code: 'LAYOUT_H2_1V3',
        name: '2 columns, left split into 3 rows',
        aliases: ['left three stacked'],
        wrapper: new Layout_h2_1v3(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2_2V3,
        code: 'LAYOUT_H2_2V3',
        name: '2 columns, right split into 3 rows',
        aliases: ['right three stacked', 'one big left three right'],
        wrapper: new Layout_h2_2v3(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V2_2H3,
        code: 'LAYOUT_V2_2H3',
        name: '2 rows, bottom split into 3 columns',
        aliases: ['one on top three below'],
        wrapper: new Layout_v2_2h3(),
    },
    {
        id: PredefinedDashboard.LAYOUT_V2_1H2_2V2,
        code: 'LAYOUT_V2_1H2_2V2',
        name: '2 rows, top split into 2 columns and bottom into 2 rows',
        aliases: [],
        wrapper: new Layout_v2_1h2_2v2(),
    },
    {
        id: PredefinedDashboard.LAYOUT_H2_1H2_2V2,
        code: 'LAYOUT_H2_1H2_2V2',
        name: '2 columns, left split into 2 columns and right into 2 rows',
        aliases: [],
        wrapper: new Layout_h2_1h2_2v2(),
    },
]

export const LAYOUT_PRESETS: LayoutPreset[] = PRESET_SEEDS.map((seed) => {
    const root = seed.wrapper.dashboardConfig?.panes
    return {
        ...seed,
        paneCount: root ? leafCount(root) : 1,
        orientation: root && root.panes.length ? unitFor(root) : null,
        structure: root && root.panes.length ? describeNode(root) : 'single pane',
    }
})

const PRESETS_BY_CODE = new Map(LAYOUT_PRESETS.map((preset) => [preset.code, preset]))

function normalize(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[_\-]+/g, ' ')
        .replace(/\s+/g, ' ')
}

/** Product convention: a "horizontal" split produces side-by-side columns. */
export function parseOrientation(value: unknown): LayoutOrientation | null {
    const text = normalize(String(value ?? ''))
    if (!text) return null
    if (['columns', 'column', 'cols', 'horizontal', 'side by side', 'left right', 'vertical divider'].includes(text)) {
        return 'columns'
    }
    if (['rows', 'row', 'vertical', 'stacked', 'top bottom', 'horizontal divider'].includes(text)) {
        return 'rows'
    }
    return null
}

export function findPresetByCode(code: unknown): LayoutPreset | undefined {
    const raw = String(code ?? '').trim()
    if (!raw) return undefined
    return PRESETS_BY_CODE.get(raw.toUpperCase().replace(/[\s-]+/g, '_'))
}

/**
 * Resolves whatever the agent sent — a preset code, a natural-language alias, or
 * a pane count plus orientation — to a concrete preset.
 */
export function resolveLayoutPreset(input: {
    code?: unknown
    name?: unknown
    panes?: unknown
    orientation?: unknown
}): LayoutPreset | undefined {
    const byCode = findPresetByCode(input.code) ?? findPresetByCode(input.name)
    if (byCode) return byCode

    const label = normalize(String(input.name ?? input.code ?? ''))
    if (label) {
        const byName = LAYOUT_PRESETS.find((preset) => normalize(preset.name) === label)
        if (byName) return byName
        const byAlias = LAYOUT_PRESETS.find((preset) => preset.aliases.some((alias) => normalize(alias) === label))
        if (byAlias) return byAlias
    }

    const paneCount = Number(input.panes)
    if (Number.isFinite(paneCount) && paneCount >= 1) {
        const orientation = parseOrientation(input.orientation) ?? 'columns'
        const exact = LAYOUT_PRESETS.find(
            (preset) => preset.paneCount === paneCount && preset.orientation === orientation,
        )
        if (exact) return exact
        if (paneCount === 1) return findPresetByCode('LAYOUT_1')
        if (paneCount === 4) return findPresetByCode('LAYOUT_H2_1V2_2V2')
        const anyWithCount = LAYOUT_PRESETS.find((preset) => preset.paneCount === paneCount)
        if (anyWithCount) return anyWithCount
    }

    return undefined
}

/** Matches the live pane tree against the presets so context can name it. */
export function detectPresetCode(root: MultiPaneItem | undefined): string | null {
    if (!root) return null
    const signature = (node: MultiPaneItem): string =>
        node.panes.length ? `${node.horizontal ? 'H' : 'V'}[${node.panes.map(signature).join(',')}]` : '*'
    const target = signature(root)
    const match = LAYOUT_PRESETS.find((preset) => {
        const presetRoot = preset.wrapper.dashboardConfig?.panes
        return presetRoot ? signature(presetRoot) === target : false
    })
    return match?.code ?? null
}

export function layoutPresetCatalog(): Array<{
    code: string
    name: string
    panes: number
    structure: string
}> {
    return LAYOUT_PRESETS.map((preset) => ({
        code: preset.code,
        name: preset.name,
        panes: preset.paneCount,
        structure: preset.structure,
    }))
}
